/* eslint-disable react-native/no-inline-styles */
// @flow

import _ from 'lodash';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { isToolbarButtonEnabled } from '../../base/config/functions.web';
import { openDialog } from '../../base/dialog';
import {
    IconCloseCircle,
    IconCrown,
    IconMessage,
    IconMicDisabled,
    IconMuteEveryoneElse,
    IconVideoOff
} from '../../base/icons';
import { PARTICIPANT_ROLE, getLocalParticipant, isLocalParticipantModerator, isParticipantModerator } from '../../base/participants';
import { getIsParticipantAudioMuted, getIsParticipantVideoMuted } from '../../base/tracks';
import SendToRoomButton from '../../breakout-rooms/components/web/SendToRoomButton';
import { PARTICIPANT_MENU_BUTTONS as BUTTONS } from '../../breakout-rooms/constants';
import { getBreakoutRooms, getCurrentRoomId } from '../../breakout-rooms/functions';
import { openChat } from '../../chat/actions';
import { ForceMuteRemoteDialog, ForceMuteRemoteParticipantVideoDialog, GrantModeratorDialog,
    KickRemoteParticipantDialog, MuteEveryoneDialog } from '../../video-menu';
import MuteRemoteParticipantsVideoDialog from '../../video-menu/components/web/MuteRemoteParticipantsVideoDialog';
import { getComputedOuterHeight } from '../functions';

import {
    ContextMenu,
    ContextMenuIcon,
    ContextMenuItem,
    ContextMenuItemGroup,
    ignoredChildClassName
} from './styled';


type Props = {

    /**
     * Callback used to open a confirmation dialog for audio muting.
     */
    muteAudio: Function,

    /**
     * Target elements against which positioning calculations are made.
     */
    offsetTarget: HTMLElement,

    /**
     * Callback for the mouse entering the component.
     */
    onEnter: Function,

    /**
     * Callback for the mouse leaving the component.
     */
    onLeave: Function,

    /**
     * Callback for making a selection in the menu.
     */
    onSelect: Function,

    /**
     * Participant reference.
     */
    participant: Object
};

export const MeetingParticipantContextMenu = ({
    offsetTarget,
    onEnter,
    onLeave,
    onSelect,
    muteAudio,
    participant
}: Props) => {
    const dispatch = useDispatch();
    const containerRef = useRef(null);
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const lockaudio = useSelector(state => state['features/base/conference']?.lockAudio);
    const lockvideo = useSelector(state => state['features/base/conference']?.lockVideo);
    const isChatButtonEnabled = useSelector(isToolbarButtonEnabled('chat'));
    const isParticipantVideoMuted = useSelector(getIsParticipantVideoMuted(participant));
    const isParticipantAudioMuted = useSelector(getIsParticipantAudioMuted(participant));
    const [ isHidden, setIsHidden ] = useState(true);
    const { jwt = null } = useSelector(state => state['features/base/jwt']);
    const { t } = useTranslation();
    const isPersonModerator = isParticipantModerator(participant);
    const rooms = useSelector(getBreakoutRooms);
    const state1 = useSelector(state => state);

    useLayoutEffect(() => {

        if (participant
            && containerRef.current
            && offsetTarget?.offsetParent
            && offsetTarget.offsetParent instanceof HTMLElement
        ) {
            const { current: container } = containerRef;
            const { offsetTop, offsetParent: { offsetHeight, scrollTop } } = offsetTarget;
            const outerHeight = getComputedOuterHeight(container);


            container.style.top = offsetTop + outerHeight > offsetHeight + scrollTop + 100
                ? (offsetTop - outerHeight) <= -300 ? offsetTop - outerHeight : Math.abs(offsetTop - outerHeight)
                : offsetTop;

            setIsHidden(false);
        } else {
            setIsHidden(true);
        }
    }, [ participant, offsetTarget ]);


    const grantModerator = useCallback(() => {
        if (_.isNil(jwt)) {
            return;
        }
        dispatch(openDialog(GrantModeratorDialog, {
            participantID: participant.id
        }));
    }, [ dispatch, participant ]);

    const kick = useCallback(() => {
        dispatch(openDialog(KickRemoteParticipantDialog, {
            participantID: participant.id
        }));
    }, [ dispatch, participant ]);

    const muteEveryoneElse = useCallback(() => {
        dispatch(openDialog(MuteEveryoneDialog, {
            exclude: [ participant.id ]
        }));
    }, [ dispatch, participant ]);

    const muteVideo = useCallback(() => {
        dispatch(openDialog(MuteRemoteParticipantsVideoDialog, {
            participantID: participant.id
        }));
    }, [ dispatch, participant ]);

    const forceMuteVideo = useCallback(() => {
        dispatch(openDialog(ForceMuteRemoteParticipantVideoDialog, {
            participantID: participant.id
        }));
    }, [ dispatch, participant ]);

    const forceMuteAudio = useCallback(() => {
        dispatch(openDialog(ForceMuteRemoteDialog, {
            participantID: participant.id
        }));
    }, [ dispatch, participant ]);


    // const forceMuteAudioeveryOne = useCallback(() => {
    //     dispatch(openDialog(ForceMuteEveryoneDialog, {
    //         participantID: participant.id
    //     }));
    // }, [ dispatch, participant ]);


    const sendPrivateMessage = useCallback(() => {
        dispatch(openChat(participant));
    }, [ dispatch, participant ]);

    const onBreakoutRoomButtonClick = () => {
        // DO something
    };
    const _currentRoomId = getCurrentRoomId(state1);
    const localParticipant = getLocalParticipant(state1);

    const _isModerator = Boolean(localParticipant?.role === PARTICIPANT_ROLE.MODERATOR);

    const _rooms = Object.values(rooms);
    const breakoutRoomsButtons = [];


    const getButtonProps = key => {
        return {
            key,
            participantID: participant?.id
        };
    };

    if (_isModerator) {
        _rooms.forEach(room => {
            if (room.id !== _currentRoomId) {
                breakoutRoomsButtons.push(
                    <SendToRoomButton
                        { ...getButtonProps(BUTTONS.SEND_PARTICIPANT_TO_ROOM) }
                        key = { room.id }
                        onClick = { onBreakoutRoomButtonClick }
                        room = { room } />
                );
            }
        });
    }


    if (!participant) {
        return null;
    }


    return (
        <>
            <ContextMenu
                className = { ignoredChildClassName }
                innerRef = { containerRef }
                isHidden = { isHidden }
                onClick = { onSelect }
                onMouseEnter = { onEnter }
                onMouseLeave = { onLeave }
                overflow = { true }>
                <ContextMenuItemGroup>
                    {isLocalModerator && (
                        <>
                            {!isParticipantAudioMuted
                         && <ContextMenuItem onClick = { muteAudio(participant) }>
                             <ContextMenuIcon src = { IconMicDisabled } />
                             <span>{t('dialog.muteParticipantButton')}</span>
                         </ContextMenuItem>}
                            <ContextMenuItem onClick = { muteEveryoneElse }>
                                <ContextMenuIcon src = { IconMuteEveryoneElse } />
                                <span>{t('toolbar.accessibilityLabel.muteEveryoneElse')}</span>
                            </ContextMenuItem>
                        </>
                    )}


                    {/* {isLocalModerator && (
                    <ContextMenuItem onClick = { forceMuteAudioeveryOne }>
                        <ContextMenuIcon src = { IconMuteEveryoneElse } />
                        <span>{Object.values(lockaudio).length === 0 || lockaudio[`${participant?.id}`]
                            ? t('participantsPane.actions.forceMuteEveryoneElse')
                            : t('participantsPane.actions.unforceMuteEveryoneElse') }</span>
                    </ContextMenuItem>
                )} */}
                </ContextMenuItemGroup>
                <ContextMenuItemGroup>
                    {isLocalModerator && (
                        <ContextMenuItem
                            disable = { _.isNil(jwt) }
                            onClick = { grantModerator }>
                            <ContextMenuIcon src = { IconCrown } />
                            <span>{isPersonModerator ? t('dialog.revokeModerationTitle') : t('toolbar.accessibilityLabel.grantModerator')}</span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator
                && (isParticipantVideoMuted || (
                    <ContextMenuItem onClick = { muteVideo }>
                        <ContextMenuIcon src = { IconVideoOff } />
                        <span>{t('participantsPane.actions.stopVideo')}</span>
                    </ContextMenuItem>
                ))}
                    {isLocalModerator && (
                        <ContextMenuItem onClick = { forceMuteVideo }>
                            <ContextMenuIcon src = { IconVideoOff } />
                            <span>{lockvideo[`${participant?.id}`] !== undefined
                            && lockvideo[`${participant?.id}`] === true
                                ? t('participantsPane.actions.unForceMuteVideo')
                                : t('participantsPane.actions.forceMuteVideo')}</span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator && (
                        <ContextMenuItem onClick = { forceMuteAudio }>
                            <ContextMenuIcon src = { IconMicDisabled } />
                            <span>{lockaudio[`${participant?.id}`] !== undefined
                            && lockaudio[`${participant?.id}`] === true
                                ? t('participantsPane.actions.unForceMute')
                                : t('participantsPane.actions.forceMute') }</span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator && (
                        <ContextMenuItem onClick = { kick }>
                            <ContextMenuIcon src = { IconCloseCircle } />
                            <span>{t('videothumbnail.kick')}</span>
                        </ContextMenuItem>
                    )}
                    {isChatButtonEnabled && (
                        <ContextMenuItem onClick = { sendPrivateMessage }>
                            <ContextMenuIcon src = { IconMessage } />
                            <span>{t('toolbar.accessibilityLabel.privateMessage')}</span>
                        </ContextMenuItem>
                    )}

                </ContextMenuItemGroup>
                {
                    breakoutRoomsButtons.length > 0
                && (
                    <ContextMenuItemGroup>
                        <div style = {{ padding: '10px 16px' }}>
                            {t('breakoutRooms.actions.sendToBreakoutRoom')}
                        </div>
                        {breakoutRoomsButtons}
                    </ContextMenuItemGroup>
                )}
            </ContextMenu>
        </>
    );
};
